import { useCallback, useEffect, useState } from 'react'

import { api, can, type ConfigDomain, type ConfigSource, type ConfigState, type LicenceStatus, type ProbeResult } from '../../lib/api'
import { ErrorBox, Loading } from '../../components/DataTable'
import { LicenceLine } from '../../components/LicenceLine'
import { Status, type Tone } from '../../components/kit'
import { ReasonTip } from '../../components/vendor/ReasonTip'
import '../../styles/sweep.css'

// The pairing with FOCTTA's consent product, and what is read from it.
//
// # WHY THIS IS ITS OWN TAB
//
// Because it governs every other tab. Somebody about to configure mail needs
// to know whether mail is read from here at all, and the answer belongs on a
// page about the pairing rather than repeated as a warning on five others.
//
// # WHAT "IN CHARGE" AND "LAST CONFIRMED" ADD
//
// A setting that says "inherited" says what somebody chose. It does not say
// whether anybody has ever arrived from Consent, or whether Consent's carrier
// has ever taken a message. So each row says who is in charge right now and
// when something happened that could only have happened if that were true.
// A row nothing has confirmed says so, and says what would confirm it.
//
// # WHY THE API'S SENTENCES OPEN FROM AN (i)
//
// Every row carries two sentences the API writes: why this is the source, and
// how it was or would be confirmed. Printed under each cell they made the table
// a page of prose. Each row now shows who is in charge and when, and the two
// sentences open, word for word, beside the answer they explain.
//
// # WHY THE LICENCE LINE SITS ABOVE THE PAIRING
//
// Because the pairing does not let a workspace run: the licence does (release
// 3). Core's switch can only switch a workspace off. So the first thing this
// tab says is whether the licence holds, until when, and how many seats are
// taken; see components/LicenceLine.tsx.

const SOURCE_WORD: Record<ConfigSource, string> = {
  INHERIT: 'From the platform',
  LOCAL: 'Set here',
  OFF: 'Not in use',
}

// The same three choices, named for a pairing FOCTTA Core made: each setting is
// inherited from Core, or configured in TPRM instead (founder, 28 Sep 2026).
const CORE_SOURCE_WORD: Record<ConfigSource, string> = {
  INHERIT: 'Inherited from FOCTTA Core',
  LOCAL: 'Configured in TPRM',
  OFF: 'Not in use',
}

// What Core currently supplies, in words. Never a credential: Core sends none.
function inheritedWords(d: ConfigDomain): string {
  const v = d.inherited?.value ?? {}
  const str = (k: string): string => (typeof v[k] === 'string' ? (v[k] as string) : '')
  switch (d.domain) {
    case 'IDENTITY':
      return str('issuer') ? 'Signs people in through ' + str('issuer') : 'Core offers no single sign-on at the moment'
    case 'MAIL':
      return str('carrier')
        ? 'Sent through Core with ' + str('carrier') + (str('sender') ? ', from ' + str('sender') : '')
        : 'Core has no mail carrier'
    case 'LIFECYCLE':
      return str('lifecycle') === 'signals'
        ? 'A person switched off in Core is switched off here'
        : 'Core does not tell TPRM about leavers'
    default:
      return ''
  }
}

// When, in words. Exact to the minute is noise on a settings screen; the
// question is "recently, or not for weeks".
function sinceWords(iso?: string): string {
  if (!iso) return 'Not yet'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return 'Not yet'
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60000))
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return minutes === 1 ? 'A minute ago' : minutes + ' minutes ago'
  const hours = Math.round(minutes / 60)
  if (hours < 24) return hours === 1 ? 'An hour ago' : hours + ' hours ago'
  const days = Math.round(hours / 24)
  return days === 1 ? 'Yesterday' : days + ' days ago'
}

function chargeTone(d: ConfigDomain): Tone {
  if (d.degraded) return 'warn'
  if (d.inCharge === 'CONSENT') return 'info'
  if (d.inCharge === 'NOBODY') return 'neutral'
  return 'ok'
}

// The API's origin note is two sentences. The heading keeps one line for each
// of the same four cases, in the order the API decides them, and the note
// itself opens beside it.
function originLine(s: ConfigState): string {
  if (s.origin === 'STANDALONE') return 'Licensed on its own, so everything is configured here'
  if (s.link) return 'Paired with the platform, so inherited settings are read from there'
  if (s.origin === 'ATTACHED') return 'The pairing is gone, so everything falls back to this product'
  return 'Provisioned by the platform, with no pairing yet'
}

// A check's answer is one of three, and unproven stays its own label: folding
// it into passed is how a screen claims the pairing works when nobody checked.
function checkLabel(c: { ok: boolean; unproven?: boolean }): { tone: Tone; word: string } {
  if (c.unproven) return { tone: 'warn', word: 'Unproven' }
  if (c.ok) return { tone: 'ok', word: 'Passed' }
  return { tone: 'crit', word: 'Refused' }
}

export function PlatformPairing() {
  const [state, setState] = useState<ConfigState | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState('')
  const [probe, setProbe] = useState<ProbeResult | null>(null)
  const [editing, setEditing] = useState(false)
  const [licence, setLicence] = useState<LicenceStatus | null>(null)
  const [licenceUnread, setLicenceUnread] = useState(false)

  const load = useCallback(() => {
    api.configState().then(setState).catch(setError)
  }, [])
  useEffect(load, [load])
  // Its own request, so a licence line that cannot be read says so on its own
  // line and does not take the pairing screen with it.
  useEffect(() => {
    api
      .licenceStatus()
      .then(setLicence)
      .catch(() => setLicenceUnread(true))
  }, [])

  if (error != null) return <ErrorBox error={error} what="The platform pairing" />
  if (!state) return <Loading what="the pairing" />

  const mayWrite = can('config.write')
  const provisioned = state.link?.provisioned === true
  const relayable = provisioned
    ? state.domains.filter((d) => (d.domain === 'MAIL' || d.domain === 'MESSAGING') && d.source === 'INHERIT')
    : []

  return (
    <div className="settings-stack">
      {(licence || licenceUnread) && (
        <section className="panel">
          {licence ? (
            <LicenceLine status={licence} />
          ) : (
            <p className="note warn">The licence could not be read just now. Reload the page to try again.</p>
          )}
        </section>
      )}
      <section className="panel">
        <div className="p1-head">
          <div>
            <h2>{provisioned && state.link ? state.link.displayName : 'FOCTTA Platform'}</h2>
            {provisioned ? (
              <p className="obs-what">Made in Consent when third-party risk was licensed, so it is changed there</p>
            ) : (
              <p className="obs-what sw-row">
                {originLine(state)}
                <ReasonTip label="Where settings come from" text={state.originNote} />
              </p>
            )}
          </div>
          {mayWrite && !provisioned && (
            <div className="p1-actions">
              <button type="button" className={'button' + (state.link ? '' : ' primary')} onClick={() => setEditing((v) => !v)}>
                {state.link ? 'Change the pairing' : 'Pair with the platform'}
              </button>
            </div>
          )}
        </div>

        {state.link && (
          <>
            <div className="std-meta">
              {state.link.status === 'LIVE' ? (
                <Status tone="ok">Answering</Status>
              ) : state.link.status === 'FAILING' ? (
                <Status tone="warn">Not answering</Status>
              ) : state.link.status === 'PAUSED' ? (
                <Status tone="neutral">Paused</Status>
              ) : (
                <Status tone="neutral">Not proved</Status>
              )}
              {provisioned && (
                <span className="sw-row">
                  Opening from Consent
                  {state.link.launchState === 'ENABLED' ? (
                    <Status tone="ok">On</Status>
                  ) : (
                    <Status tone="warn">Switched off</Status>
                  )}
                </span>
              )}
              <span className="chip mono">{state.link.baseUrl}</span>
              <span className="chip mono">{state.link.remoteTenantId}</span>
              {!provisioned && <span className="chip mono">{state.link.tokenRef}</span>}
              {state.link.consecutiveFailures > 0 && (
                <span className="chip warn">
                  {state.link.consecutiveFailures}{' '}
                  {state.link.consecutiveFailures === 1 ? 'failure' : 'failures'} in a row
                </span>
              )}
            </div>
            {provisioned && state.link.launchState === 'DISABLED' && state.link.launchReason && (
              <p className="note warn">{state.link.launchReason}</p>
            )}
            {state.link.lastError && !probe && <p className="note warn">{state.link.lastError}</p>}
            {mayWrite && (
              <div className="p1-actions save-row">
                <button
                  type="button"
                  className="button primary"
                  disabled={busy !== ''}
                  onClick={async () => {
                    setBusy('test')
                    try {
                      setProbe(await api.testPlatformLink())
                      load()
                    } finally {
                      setBusy('')
                    }
                  }}
                  data-action="tprm.settings-platform.a01"
                >
                  {busy === 'test' ? 'Asking' : 'Test the pairing'}
                </button>
                {!provisioned && (
                  <button
                    type="button"
                    className="button ghost"
                    disabled={busy !== ''}
                    onClick={async () => {
                      setBusy('unpair')
                      try {
                        await api.unpairPlatform()
                        setProbe(null)
                        load()
                      } finally {
                        setBusy('')
                      }
                    }}
                    data-action="tprm.settings-platform.a02"
                  >
                    Unpair
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {probe && (
          <div className={'note' + (probe.allOk ? ' ok' : ' warn')} role="status">
            <strong>{probe.note}</strong>
            <ul className="probe-list">
              {probe.checks.map((c) => {
                const label = checkLabel(c)
                return (
                  <li key={c.check} className={c.unproven ? 'probe-open' : c.ok ? 'probe-ok' : 'probe-no'}>
                    <Status tone={label.tone}>{label.word}</Status>
                    <span>
                      <strong>{c.check}</strong> {c.detail}
                    </span>
                  </li>
                )
              })}
            </ul>
          </div>
        )}

        {editing && mayWrite && !provisioned && (
          <PairForm
            link={state.link}
            onDone={() => {
              setEditing(false)
              load()
            }}
          />
        )}
      </section>

      <section className="panel">
        <div className="p1-head">
          <div>
            <h2>Who is in charge</h2>
            <p className="obs-what">Each is configured once: who is in charge now, and when that was last shown</p>
          </div>
        </div>

        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>What</th>
                <th>In charge</th>
                <th>Last confirmed</th>
                {mayWrite && <th>Read it from</th>}
              </tr>
            </thead>
            <tbody>
              {state.domains.map((d) => (
                <tr key={d.domain}>
                  <td>
                    {d.href ? <a href={d.href}>{d.name}</a> : d.name}
                    <span className="cell-sub">{d.what}</span>
                    {provisioned && d.source === 'INHERIT' && d.inherited && (
                      <span className="cell-sub">
                        {inheritedWords(d)}
                        {!d.inherited.live && ' (last read ' + sinceWords(d.inherited.seenAt).toLowerCase() + ')'}
                      </span>
                    )}
                  </td>
                  <td>
                    <span className="sw-row">
                      <Status tone={chargeTone(d)}>{d.inChargeName || SOURCE_WORD[d.source]}</Status>
                      <ReasonTip label={'Why this source: ' + d.name} text={d.why} />
                    </span>
                  </td>
                  <td>
                    <span className="sw-row">
                      {d.confirmedAt ? (
                        <span title={new Date(d.confirmedAt).toLocaleString()}>{sinceWords(d.confirmedAt)}</span>
                      ) : (
                        <span className="muted">Not yet</span>
                      )}
                      <ReasonTip
                        label={(d.confirmedAt ? 'How it was confirmed: ' : 'What would confirm it: ') + d.name}
                        text={d.confirmedHow}
                      />
                    </span>
                  </td>
                  {mayWrite && (
                    <td>
                      <div className="chiprow">
                        {(['INHERIT', 'LOCAL', 'OFF'] as ConfigSource[]).map((s) => (
                          <button
                            key={s}
                            type="button"
                            className="chip-toggle"
                            aria-pressed={(d.asked ?? d.source) === s}
                            disabled={busy === d.domain || (s === 'INHERIT' && d.inheritable === false)}
                            title={
                              s === 'INHERIT' && d.inheritable === false
                                ? 'People and their personas are always set here, in People'
                                : undefined
                            }
                            onClick={async () => {
                              setBusy(d.domain)
                              try {
                                await api.setConfigSource(d.domain, s)
                                load()
                              } finally {
                                setBusy('')
                              }
                            }}
                            data-action="tprm.settings-platform.a03"
                          >
                            {provisioned ? CORE_SOURCE_WORD[s] : SOURCE_WORD[s]}
                          </button>
                        ))}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {mayWrite && relayable.length > 0 && <RelayTest domains={relayable} onSent={load} />}
      </section>
    </div>
  )
}

// Sending one message through Consent's carrier. The only way to confirm that
// inherited mail leaves is to send something, so this sends something, and
// shows what Consent's carrier said about it.
function RelayTest({ domains, onSent }: { domains: ConfigDomain[]; onSent: () => void }) {
  const channels: { value: 'EMAIL' | 'SMS' | 'WHATSAPP'; label: string }[] = []
  if (domains.some((d) => d.domain === 'MAIL')) channels.push({ value: 'EMAIL', label: 'Email' })
  if (domains.some((d) => d.domain === 'MESSAGING')) {
    channels.push({ value: 'SMS', label: 'SMS' }, { value: 'WHATSAPP', label: 'WhatsApp' })
  }
  const [channel, setChannel] = useState(channels[0]?.value ?? 'EMAIL')
  const [to, setTo] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<{ sent: boolean; note: string; detail?: string } | null>(null)

  return (
    <form
      className="wizard-form"
      onSubmit={async (e) => {
        e.preventDefault()
        setBusy(true)
        setResult(null)
        try {
          const out = await api.relayTest(channel, to.trim())
          setResult(out)
          if (out.sent) onSent()
        } catch (err) {
          const any = err as { message?: string }
          setResult({ sent: false, note: any.message ?? 'The test could not be sent.' })
        } finally {
          setBusy(false)
        }
      }}
    >
      <div className="field-row two">
        <label>
          Send a test through Consent
          <select value={channel} onChange={(e) => setChannel(e.target.value as typeof channel)}>
            {channels.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          {channel === 'EMAIL' ? 'To this address' : 'To this number'}
          <input
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder={channel === 'EMAIL' ? 'you@example.com' : '+919800000000'}
            required
          />
        </label>
      </div>
      <div className="p1-actions">
        <button type="submit" className="button ghost" disabled={busy} data-action="tprm.settings-platform.a04">
          {busy ? 'Sending' : 'Send a test'}
        </button>
      </div>
      {result && (
        <p className={'note sw-row' + (result.sent ? ' ok' : ' warn')} role="status">
          <Status tone={result.sent ? 'ok' : 'crit'}>{result.sent ? 'Sent' : 'Not sent'}</Status>
          <span>
            {result.detail ? result.detail + ' ' : ''}
            {result.note}
          </span>
        </p>
      )}
    </form>
  )
}

function PairForm({ link, onDone }: { link?: ConfigState['link']; onDone: () => void }) {
  const [baseUrl, setBaseUrl] = useState(link?.baseUrl ?? 'https://app.foctta.com')
  const [remoteTenantId, setRemote] = useState(link?.remoteTenantId ?? '')
  const [tokenRef, setTokenRef] = useState(link?.tokenRef ?? 'env:PLATFORM_TOKEN')
  const [busy, setBusy] = useState(false)
  const [issues, setIssues] = useState<string[]>([])

  return (
    <form
      className="wizard-form"
      onSubmit={async (e) => {
        e.preventDefault()
        setBusy(true)
        setIssues([])
        try {
          await api.savePlatformLink({ baseUrl, remoteTenantId, tokenRef })
          onDone()
        } catch (err) {
          const any = err as { issues?: { detail: string }[]; message?: string }
          setIssues(any.issues?.map((i) => i.detail) ?? [any.message ?? 'That was not saved.'])
        } finally {
          setBusy(false)
        }
      }}
    >
      <div className="field-row two">
        <label>
          Where the platform is
          <input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} required />
        </label>
        <label>
          Its tenant identifier
          <input
            value={remoteTenantId}
            onChange={(e) => setRemote(e.target.value)}
            placeholder="00000000-0000-4000-9100-000000000001"
            required
          />
          <span className="field-note">The tenant on the platform this one corresponds to</span>
        </label>
      </div>
      <label>
        Where the service token lives
        <input value={tokenRef} onChange={(e) => setTokenRef(e.target.value)} required />
        <span className="field-note">As env:NAME or secret:NAME, never the token itself, which is not stored here</span>
      </label>
      {issues.map((i) => (
        <p key={i} className="note warn">
          {i}
        </p>
      ))}
      <div className="p1-actions">
        <button type="submit" className="button primary" disabled={busy} data-action="tprm.settings-platform.a05">
          {busy ? 'Saving' : 'Save the pairing'}
        </button>
      </div>
    </form>
  )
}
